import React, { useEffect } from 'react';
import {
    Alert,
    AlertTitle,
    Snackbar,
    IconButton,
    Stack,
    Slide,
    Paper,
    Typography,
    Box
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { useNotificationSocket } from '../hooks/useSocket';

function SlideTransition(props: TransitionProps & {
    children: React.ReactElement<any, any>;
}) {
    return <Slide {...props} direction="left" />;
}

const getIcon = (type: string) => {
    switch (type) {
        case 'success':
            return <SuccessIcon sx={{ mr: 1 }} />;
        case 'error':
            return <ErrorIcon sx={{ mr: 1 }} />;
        case 'warning':
            return <WarningIcon sx={{ mr: 1 }} />;
        default:
            return <InfoIcon sx={{ mr: 1 }} />;
    }
};

const getSeverity = (type: string): 'success' | 'error' | 'warning' | 'info' => {
    switch (type) {
        case 'success':
            return 'success';
        case 'error':
            return 'error';
        case 'warning':
            return 'warning';
        default:
            return 'info';
    }
};

const Notifications: React.FC = () => {
    const { notifications, removeNotification } = useNotificationSocket();
    const [open, setOpen] = React.useState(false);
    const [currentNotification, setCurrentNotification] = React.useState<any>(null);

    useEffect(() => {
        if (notifications.length > 0 && !currentNotification) {
            setCurrentNotification(notifications[0]);
            setOpen(true);
        }
    }, [notifications, currentNotification]);

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    const handleExited = () => {
        removeNotification(0);
        setCurrentNotification(null);
    };

    if (!currentNotification) return null;

    return (
        <>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={open}
                autoHideDuration={6000}
                onClose={handleClose}
                TransitionComponent={SlideTransition}
                TransitionProps={{
                    onExited: handleExited
                }}
            >
                <Alert
                    severity={getSeverity(currentNotification.type)}
                    variant="filled"
                    onClose={handleClose}
                    sx={{ width: '100%', maxWidth: 400 }}
                    icon={getIcon(currentNotification.type)}
                >
                    {currentNotification.title && (
                        <AlertTitle>{currentNotification.title}</AlertTitle>
                    )}
                    {currentNotification.message}
                    {currentNotification.timestamp && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                            {new Date(currentNotification.timestamp).toLocaleTimeString()}
                        </Typography>
                    )}
                </Alert>
            </Snackbar>

            {/* Notification queue indicator */}
            {notifications.length > 1 && (
                <Paper
                    sx={{
                        position: 'fixed',
                        top: 100,
                        right: 16,
                        p: 1,
                        bgcolor: 'grey.100',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                    elevation={2}
                >
                    <InfoIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                        {notifications.length - 1} more notification{notifications.length > 2 ? 's' : ''}
                    </Typography>
                </Paper>
            )}
        </>
    );
};

export default Notifications;

// Notification Center Component for viewing all notifications
export const NotificationCenter: React.FC<{
    open: boolean;
    onClose: () => void;
}> = ({ open, onClose }) => {
    const { notifications, clearNotifications, removeNotification } = useNotificationSocket();

    return (
        <Paper
            sx={{
                position: 'fixed',
                right: open ? 0 : -400,
                top: 0,
                width: 400,
                height: '100vh',
                transition: 'right 0.3s ease',
                zIndex: 1200,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '-2px 0 5px rgba(0,0,0,0.1)'
            }}
        >
            <Box
                sx={{
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Typography variant="h6">Notifications</Typography>
                <Box>
                    {notifications.length > 0 && (
                        <IconButton size="small" onClick={clearNotifications} sx={{ mr: 1 }}>
                            <Typography variant="caption" sx={{ mr: 0.5 }}>Clear all</Typography>
                        </IconButton>
                    )}
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {notifications.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: 'text.secondary'
                        }}
                    >
                        <InfoIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography>No notifications</Typography>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        {notifications.map((notification, index) => (
                            <Alert
                                key={index}
                                severity={getSeverity(notification.type)}
                                onClose={() => removeNotification(index)}
                                sx={{ width: '100%' }}
                            >
                                {notification.title && (
                                    <AlertTitle>{notification.title}</AlertTitle>
                                )}
                                {notification.message}
                                {notification.timestamp && (
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                        {new Date(notification.timestamp).toLocaleString()}
                                    </Typography>
                                )}
                            </Alert>
                        ))}
                    </Stack>
                )}
            </Box>
        </Paper>
    );
};